import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listAdminPayouts, processPayablePayouts, retryPayout } from "@/lib/payouts.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/payouts")({
  head: () => ({ meta: [{ title: "Payouts — Admin" }] }),
  component: AdminPayouts,
});

type Row = {
  id: string;
  shift_id: string;
  bartender_id: string;
  amount_pence: number;
  status: string;
  attempts: number | null;
  failure_reason: string | null;
  paid_out_at: string | null;
  stripe_transfer_id: string | null;
  last_attempt_at: string | null;
  created_at: string;
  notes: string | null;
};
type Profile = { id: string; full_name: string | null; stripe_payouts_enabled: boolean | null };

function AdminPayouts() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(listAdminPayouts);
  const sweep = useServerFn(processPayablePayouts);
  const retry = useServerFn(retryPayout);

  const [rows, setRows] = useState<Row[]>([]);
  const [profs, setProfs] = useState<Profile[]>([]);
  const [filter, setFilter] = useState<"all" | "payable" | "paid_out" | "failed">("all");
  const [busy, setBusy] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);

  const refresh = async () => {
    const r = await load();
    setRows(r.rows as Row[]);
    setProfs(r.profiles as Profile[]);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await refresh();
    })();
  }, []);

  const runSweep = async () => {
    setBusy(true);
    try {
      const r = await sweep({ data: { environment: getStripeEnvironment() } });
      toast.success(`Sweep complete · ${r.paid} paid · ${r.failed} failed.`);
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Sweep failed."); }
    finally { setBusy(false); }
  };

  const handleRetry = async (id: string) => {
    setActingId(id);
    try {
      await retry({ data: { payout_id: id, environment: getStripeEnvironment() } });
      toast.success("Retry queued.");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Retry failed."); }
    finally { setActingId(null); }
  };

  const nameOf = (id: string) => profs.find((p) => p.id === id)?.full_name ?? "—";
  const connectOk = (id: string) => !!profs.find((p) => p.id === id)?.stripe_payouts_enabled;
  const filtered = rows.filter((r) => filter === "all" || r.status === filter);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Treasury</p>
            <h1 className="mt-3 font-display text-3xl">Bartender Payouts</h1>
            <p className="mt-2 text-sm text-muted-foreground">Sweep transfers payable balances to Connect accounts via Stripe. Cron runs every 15 minutes.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Back</Link>
            <Button onClick={runSweep} disabled={busy} className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury">
              {busy ? "Running…" : "Run Sweep Now"}
            </Button>
          </div>
        </div>
        <div className="gold-divider mt-4" />

        <div className="mt-6 flex gap-2 text-xs tracking-wide-luxury">
          {(["all", "payable", "paid_out", "failed"] as const).map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1 border ${filter === s ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-accent"}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        <div className="mt-6 border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left tracking-wide-luxury text-[0.65rem] text-muted-foreground">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Bartender</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Attempts</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No payouts in this view.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 align-top">
                  <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")}</td>
                  <td className="px-4 py-3">
                    <div>{nameOf(r.bartender_id)}</div>
                    {!connectOk(r.bartender_id) && <div className="text-[0.65rem] text-amber-700">Connect not enabled</div>}
                  </td>
                  <td className="px-4 py-3 font-display">£{(r.amount_pence / 100).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 border tracking-wide-luxury text-[0.6rem] ${
                      r.status === "paid_out" ? "border-emerald-700 text-emerald-700"
                        : r.status === "failed" ? "border-destructive text-destructive"
                        : r.status === "payable" ? "border-accent text-accent"
                        : "border-border text-muted-foreground"
                    }`}>{r.status}</span>
                    {r.stripe_transfer_id && (
                      <div className="mt-1 text-[0.65rem] text-muted-foreground">{r.stripe_transfer_id}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">{r.attempts ?? 0}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-[28ch]">
                    {r.failure_reason ?? r.notes ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.status === "failed" && (
                      <Button onClick={() => handleRetry(r.id)} disabled={actingId === r.id}
                        variant="outline" className="rounded-none border-border text-xs tracking-wide-luxury">
                        {actingId === r.id ? "…" : "Retry"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
